import React, { useEffect, useState } from "react";

import { CloseGreyIcon } from "../../../assets/icons";
import { useNotification } from "../../toster-notification/NotificationContext";
import { CategorySelectDto } from "../../../models/categories/categories.response";
import { categoriesService } from "../../../services/categoriesService";
import { Switch } from "@mui/material";
import { subcategoryService } from "../../../services/subcategoriesService";

interface EditSubcategoryViewProps {
    subcategoryId: string;
    localId: string;
    onClose: () => void;
}

const EditSubcategoryView: React.FC<EditSubcategoryViewProps> = ({ onClose, localId, subcategoryId }) => {

    const [subcategoryName, setSubcategoryName] = useState<string>("");
    const [subcategoryNameError, setSubcategoryNameError] = useState<string>("");
    const [subcategorySlug, setSubcategorySlug] = useState<string>("");
    const [subcategorySlugError, setSubcategorySlugError] = useState<string>("");
    const [subcategoryDescription, setSubcategoryDescription] = useState<string>("");
    const [isActive, setIsActive] = useState<boolean>(true);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
    const [selectedCategoryIdError, setSelectedCategoryIdError] = useState<string>("");

    const [selectCategories, setSelectCategories] = useState<CategorySelectDto[]>([]);

    const [loading, setLoading] = useState<boolean>(false);

    const { notify } = useNotification();


    useEffect(() => {
        fetchCategoriesSelect();
        fetchSubcategory();
    }, [localId, subcategoryId]);


    const fetchSubcategory = async () => {
        if (!localId) {
            notify("error", "Local ID nije pronađen.");
            return;
        }

        setLoading(true);
        try {
            const response = await subcategoryService.getInfoById(localId, subcategoryId);

            if (response.success && response.data) {
                setSubcategoryName(response.data.name);
                setSubcategorySlug(response.data.slug);
                setSubcategoryDescription(response.data.description);
                setIsActive(response.data.isActive);
                setSelectedCategoryId(response.data.categoryId);
            } else {
                notify("error", "Greška pri učitavanju podkategorije.");
            }
        } catch (error: any) {
            notify("error", "Greška pri učitavanju podkategorije.");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateSubcategory = async () => {

        if (!localId) {
            notify("error", "Local ID nije pronađen.");
            return;
        }

        setSubcategoryNameError("");
        setSubcategorySlugError("");
        setSelectedCategoryIdError("");

        let isValid = true;

        if (subcategoryName.trim() === "") {
            setSubcategoryNameError("Naziv podkategorije je obavezan.");
            isValid = false;
        }

        if (subcategorySlug.trim() === "") {
            setSubcategorySlugError("Slug podkategorije je obavezan.");
            isValid = false;
        }

        if (selectedCategoryId.trim() === "") {
            setSelectedCategoryIdError("Kategorija je obavezna.");
            isValid = false;
        }

        if (!isValid) {
            return;
        }

        setLoading(true);

        try {
            const response = await subcategoryService.updateSubcategory(localId, subcategoryId, {
                categoryId: selectedCategoryId,
                name: subcategoryName,
                slug: subcategorySlug,
                description: subcategoryDescription,
                isActive: isActive,
            });

            if (response.success && response.message) {
                notify("success", "Podkategorija je uspešno ažurirana.");
                onClose();
            } else {
                notify("error", "Došlo je do greške prilikom ažuriranja podkategorije.");
            }
        } catch (error: any) {
            notify("error", "Došlo je do greške prilikom ažuriranja podkategorije.");
        } finally {
            setLoading(false);
        }
    };

    const fetchCategoriesSelect = async () => {
        setLoading(true);

        try {
            const response = await categoriesService.getCategoriesSelect(localId!);

            if (response.success && response.data) {
                setSelectCategories(response.data);
            } else {
                notify("error", "Došlo je do greške prilikom učitavanja kategorija.");
            }

        } catch (error) {
            notify("error", "Došlo je do greške prilikom učitavanja kategorija.");
        } finally {
            setLoading(false);
        }
    };

    const setSlugName = (name: string) => {
        return name
            .toLowerCase()
            .replace(/š/g, 's')
            .replace(/č/g, 'c')
            .replace(/ć/g, 'c')
            .replace(/ž/g, 'z')
            .replace(/đ/g, 'dj')
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9\-]/g, '');
    }


    return (
        <section className="modal-section subcategory">
            <div className="w-layout-blockcontainer container w-container">
                <div className="modal-wrapper">
                    <div className="modal-div">
                        <div className="modal-header-div">
                            <div>
                                <div className="modal-header">Nova podkategorija</div>
                                <div className="description-text">
                                    Unesite podatke o podkategoriji<br />
                                </div>
                            </div>
                            <div className="modal-button-div" onClick={onClose}>
                                <img loading="lazy" src={CloseGreyIcon} alt="" className="icon-size-20 close" />
                            </div>
                        </div>
                        <div className="modal-form-block w-form">
                            <form className="modal-form">

                                <div className="modal-form-div">
                                    <label htmlFor="field-10" className="modal-form-label">Kategorija*</label>
                                    <select className="form-select auto-width w-select"
                                        value={selectedCategoryId}
                                        onChange={(e) => setSelectedCategoryId(e.target.value)}
                                    >

                                        {selectCategories.length > 0 ? (
                                            selectCategories.map((category) => (
                                                <option key={category.id} value={category.id}>{category.name}</option>
                                            ))
                                        ) : (
                                            <option value="">Nema dostupnih kategorija</option>
                                        )}

                                    </select>
                                    <div className="error-text">{selectedCategoryIdError}</div>
                                </div>

                                <div className="modal-form-div">
                                    <label className="modal-form-label">Naziv*</label>
                                    <input className="form-input grey w-input" type="text" placeholder="Unesite ime podkategorije"
                                        value={subcategoryName}
                                        onChange={(e) => {
                                            setSubcategorySlug(setSlugName(e.target.value));
                                            setSubcategoryName(e.target.value)
                                        }}
                                    />
                                    <div className="error-text">{subcategoryNameError}</div>
                                </div>

                                <div className="modal-form-div">
                                    <label className="modal-form-label">Slug</label>
                                    <input className="form-input grey w-input" type="text" placeholder="Unesite slug podkategorije"
                                        value={subcategorySlug}
                                        onChange={(e) => setSubcategorySlug(e.target.value)}
                                    />
                                    <div className="error-text">{subcategorySlugError}</div>
                                </div>

                                <div className="modal-form-div">
                                    <label className="modal-form-label">Opis</label>
                                    <textarea className="form-textarea grey" placeholder="Unesite opis podkategorije"
                                        value={subcategoryDescription}
                                        onChange={(e) => setSubcategoryDescription(e.target.value)}
                                    />
                                </div>

                                <div className="modal-form-div f-horizontal">
                                    <label className="modal-form-label">Akivna podkategorija*</label>
                                    <Switch
                                        checked={isActive}
                                        onChange={(e) => setIsActive(e.target.checked)}
                                    />
                                </div>

                            </form>
                        </div>
                        <div className="modal-buttons">
                            <div className="button-secondary medium" onClick={onClose}>
                                <div>Otkaži</div>
                            </div>
                            <div className="button-primary medium" onClick={() => handleUpdateSubcategory()}>
                                <div>Ažuriraj</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default EditSubcategoryView;